/**
 * The dashboard's tabs and who may open each. A tab is shown only to a login holding its own
 * dashboard.* code and the permission of the screens its numbers drill into; the API enforces the
 * same pair. A login with one tab sees no tab bar, just its dashboard.
 */
import type { Permission } from "@nexseed/shared";

// In the order the seed moves: an admin's bar reads Gate → Arrival → QA → Drying → Packaging.
export const DASHBOARD_TABS = [
  "overview",
  "gate",
  "arrival",
  "qaSupervisor",
  "qaManager",
  "labManager",
  "got",
  "drying",
  "packaging",
  "stores",
] as const;
export type DashboardTab = (typeof DASHBOARD_TABS)[number];

export const TAB_META: Record<
  DashboardTab,
  {
    label: string;
    /** The tab's own code (0076): an admin switches each tab on per role. */
    permission: Permission;
    /** The module its numbers drill into; the API asks for it too. */
    module: Permission;
    period: boolean;
  }
> = {
  // One dashboard per QA role (user, 2026-10-21): a QA Supervisor sees only qaSupervisor, a QA
  // Manager only qaManager, an admin both among the rest (docs/designs/qa-dashboard.md).
  qaSupervisor: {
    label: "QA Supervisor",
    permission: "dashboard.qa_supervisor",
    module: "qc.view",
    period: false,
  },
  qaManager: {
    label: "QA Manager",
    permission: "dashboard.qa_manager",
    module: "qc.view",
    period: false,
  },
  // The Lab Manager's germination dashboard (user, 2026-10-22): its own role, its own tab.
  labManager: {
    label: "Lab Manager",
    permission: "dashboard.lab_manager",
    module: "qc.view",
    period: false,
  },
  // The GOT Manager's tab (user, 2026-11-03): the GOT pipeline, overdue samples and results.
  got: {
    label: "GOT",
    permission: "dashboard.got",
    module: "got.review",
    period: true,
  },
  overview: {
    label: "Overview",
    permission: "dashboard.overview",
    module: "document.view",
    period: true,
  },
  gate: {
    label: "Gate",
    permission: "dashboard.gate",
    module: "gate.view",
    period: true,
  },
  arrival: {
    label: "Arrival & Storage",
    permission: "dashboard.arrival",
    module: "document.view",
    period: true,
  },
  // Quality and QC Supervisor left the tabs for the QA dashboards (docs/designs/qa-dashboard.md,
  // eng review D5): the QA Manager's results block draws the Quality panels.
  drying: {
    label: "Drying & Processing",
    permission: "dashboard.processing",
    module: "document.view",
    period: true,
  },
  // A placeholder until its numbers exist; /dashboard/packaging is where they will come from.
  packaging: {
    label: "Packaging & Dispatch",
    permission: "dashboard.packaging",
    module: "document.view",
    period: false,
  },
  // The Stores Manager's tab (2026-10-14): stock, reorder, work waiting and the period's movement.
  stores: {
    label: "Stores",
    permission: "dashboard.stores",
    module: "stores.view",
    period: true,
  },
};

export function visibleTabs(can: (p: Permission) => boolean): DashboardTab[] {
  return DASHBOARD_TABS.filter(
    (t) => can(TAB_META[t].permission) && can(TAB_META[t].module)
  );
}

export const isQaTab = (
  t: DashboardTab
): t is "qaSupervisor" | "qaManager" | "labManager" =>
  t === "qaSupervisor" || t === "qaManager" || t === "labManager";
